import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/** Always leave the WebView (cart, enrollment, payments, coaching shop). */
const FORCE_EXTERNAL_HOSTS = [
    'myoola.oolalife.com',
    'mybusiness.oolalife.com',
    'oolalifecoaching.com',
];

/** Portal / API hosts stay inside the app shell. */
const STAY_IN_APP_HOSTS = [
    'dashboard.oolalife.com',
    'api.oolalife.com',
    'api-staging.oolalife.com',
    'localhost',
    '127.0.0.1',
    '10.0.2.2',
];

function hostMatches(host, list) {
    return list.some((entry) => host === entry || host.endsWith(`.${entry}`));
}

export function isNativeApp() {
    return Capacitor.isNativePlatform();
}

export function shouldOpenExternally(url) {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('mailto:') || url.startsWith('tel:')) return true;

    try {
        const parsed = new URL(url, window.location.href);
        const host = parsed.hostname.toLowerCase();
        if (!host) return false;

        if (host === window.location.hostname.toLowerCase()) {
            return false;
        }
        if (hostMatches(host, STAY_IN_APP_HOSTS)) {
            return false;
        }
        if (hostMatches(host, FORCE_EXTERNAL_HOSTS)) {
            return true;
        }

        // Other absolute http(s) origins (Calendly, marketing www, etc.)
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Open a URL in the system browser on native; window.open on web.
 * Use for upgrade, enrollment, payment, and other off-portal links.
 */
export async function openExternalUrl(url) {
    if (!url) return;

    if (isNativeApp()) {
        try {
            await Browser.open({ url });
            return;
        } catch (err) {
            console.warn('[capacitor] Browser.open failed, falling back', err);
        }
    }

    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Smart open: portal paths stay in-app; commerce/external leave the WebView.
 */
export async function openUrl(url) {
    if (!url) return;
    if (shouldOpenExternally(url)) {
        await openExternalUrl(url);
        return;
    }
    if (url.startsWith('/') && !url.startsWith('//')) {
        if (typeof window.__oolaNavigate === 'function') {
            window.__oolaNavigate(url);
            return;
        }
        window.location.assign(url);
        return;
    }
    await openExternalUrl(url);
}

/**
 * Use on <a href={upgradeUrl} target="_blank"> so native opens the system browser.
 * On web, still prevents default and opens a tab (same outcome, consistent).
 */
export function onExternalAnchorClick(event, url) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }
    const href =
        url ||
        (event && event.currentTarget && event.currentTarget.href) ||
        '';
    openExternalUrl(href);
}
